import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
} from "@mui/material";
import {
  IVidro,
  ISelectOption,
  SITUACAO_OPTIONS,
  STATUS_OPTIONS,
  SIM_NAO_OPTIONS,
} from "../types/vidro.types";
import { InputDecimalPtBr } from "../../components/InputDecimalPtBr";

// Estilos customizados específicos para esta página - SEM BORDA
const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f0f0f0",
    borderRadius: 0,
    minHeight: 56,
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#3C90AD",
    fontWeight: 500,
    fontSize: "0.8rem",
    "&.Mui-focused": {
      color: "#3C90AD",
    },
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
};

const selectSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "20px 14px 8px 14px",
  },
};

const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
};

interface TabGeralProps {
  vidro: Partial<IVidro>;
  onChange: (campo: keyof IVidro, valor: any) => void;
  referencias: ISelectOption[];
  linhas: ISelectOption[];
  tiposVidro: ISelectOption[];
  arredondamentos: ISelectOption[];
  // FASE 05 — 4ª validação/Frente 9 2026-07-30: sinaliza duplicidade em tempo real no campo.
  codigoDuplicado?: boolean;
  onOpenMidias: () => void;
}

export const TabGeral: React.FC<TabGeralProps> = ({
  vidro,
  onChange,
  referencias,
  linhas,
  tiposVidro,
  arredondamentos,
  codigoDuplicado,
  onOpenMidias,
}) => {
  return (
    <Box>
      {/* FASE 05 Ajustes — PO 2026-08-22 (item 5): Vidro era o único formulário fora do padrão de
          11/08 — Situação sozinha numa linha ACIMA do container principal e Status na 3ª coluna
          da linha do Código (gabarito: Perfil/TabFicha). Situação é somente-leitura — as
          transições acontecem só pelos botões Homologar/Concluir/Disponibilizar. */}
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel sx={labelSx}>Situação</InputLabel>
            <Select
              value={vidro.idSituacao || 1}
              label="Situação"
              disabled
              sx={selectSx}
            >
              {SITUACAO_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Container principal com Imagem à esquerda e campos à direita */}
      <Grid container spacing={3}>
        {/* Coluna Esquerda - Imagem do Produto */}
        <Grid item xs={12} md={3}>
          {/* Container da Imagem - COM BORDA CINZA */}
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              p: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#333", mb: 2, fontWeight: 500 }}
            >
              Imagem do produto
            </Typography>
            
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 180,
                backgroundColor: "#fff",
              }}
            >
              {vidro.imagemUrl ? (
                <img
                  src={vidro.imagemUrl}
                  alt="Imagem do produto"
                  style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma imagem
                </Typography>
              )}
            </Box>

            {vidro.imagemNomeArquivo && (
              <Typography
                variant="caption"
                sx={{ color: "#999", textAlign: "center", display: "block", mt: 2 }}
              >
                {vidro.imagemNomeArquivo}
              </Typography>
            )}
          </Box>

          {/* Botão FORA do container da imagem */}
          <Button
            variant="contained"
            fullWidth
            onClick={onOpenMidias}
            sx={{
              backgroundColor: "#3C90AD",
              "&:hover": { backgroundColor: "#2d6f87" },
              textTransform: "none",
              py: 1.2,
            }}
          >
            GERENCIAMENTO DE MÍDIAS
          </Button>
        </Grid>

        {/* Coluna Direita - Campos do Formulário */}
        <Grid item xs={12} md={9}>
          {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: ordem do mockup (image8) — Código | Código interno,
              Descrição em linha própria, e Código interno passa a existir na tela. */}
          {/* Linha: Código, Código interno, Status (padrão de 11/08 — item 5 dos Ajustes 22/08) */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código"
                value={vidro.codigo || ""}
                onChange={(e) => onChange("codigo", e.target.value.toUpperCase())}
                required
                inputProps={{ maxLength: 50 }}
                error={codigoDuplicado}
                helperText={codigoDuplicado ? "Código já existe" : undefined}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código interno"
                value={vidro.codigoInterno || ""}
                onChange={(e) => onChange("codigoInterno", e.target.value)}
                inputProps={{ maxLength: 50 }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx}>Status</InputLabel>
                <Select
                  value={vidro.idStatus || 1}
                  label="Status"
                  onChange={(e) => onChange("idStatus", e.target.value)}
                  sx={selectSx}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Linha: Descrição */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={vidro.descricao || ""}
                onChange={(e) => onChange("descricao", e.target.value)}
                required
                inputProps={{ maxLength: 200 }}
                sx={inputSx}
              />
            </Grid>
          </Grid>

          {/* Linha: Referência, Linha, Tipo */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              {/* FASE 05 — 4ª validação/Frente 9 2026-07-30 (P2): Referência e Linha permanecem
                  obrigatórias — asterisco sinalizando. */}
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Referência (fornecedor do material)</InputLabel>
                <Select
                  value={vidro.idReferencia || ""}
                  label="Referência (fornecedor do material)"
                  onChange={(e) => onChange("idReferencia", e.target.value || undefined)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {referencias.map((ref) => (
                    <MenuItem key={ref.id} value={ref.id}>
                      {ref.nome || ref.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Linha</InputLabel>
                <Select
                  value={vidro.idLinha || ""}
                  label="Linha"
                  onChange={(e) => onChange("idLinha", e.target.value || undefined)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {linhas.map((linha) => (
                    <MenuItem key={linha.id} value={linha.id}>
                      {linha.nome || linha.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Tipo</InputLabel>
                <Select
                  value={vidro.idTipoVidro && tiposVidro.some(t => t.id === vidro.idTipoVidro) ? vidro.idTipoVidro : ""}
                  label="Tipo"
                  onChange={(e) => onChange("idTipoVidro", e.target.value === "" ? undefined : Number(e.target.value))}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {tiposVidro.map((tipo) => (
                    <MenuItem key={tipo.id} value={tipo.id}>
                      {tipo.nome || tipo.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Linha: Espessura, Peso, Fator L/H */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: decimal pt-BR igual ao Peso do Perfil —
                  o toFixed(2) anterior impedia digitar a vírgula livremente. */}
              <InputDecimalPtBr
                fullWidth
                label="Espessura (mm)"
                value={vidro.espessura}
                onChange={(v) => onChange("espessura", v ?? 0)}
                required
                placeholder="0,00"
                inputProps={{ maxLength: 12 }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputDecimalPtBr
                fullWidth
                label="Peso (kg/m²)"
                value={vidro.peso}
                onChange={(v) => onChange("peso", v ?? 0)}
                required
                placeholder="0,00"
                inputProps={{ maxLength: 12 }}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Fator L/H"
                type="number"
                value={vidro.fatorLH || ""}
                onChange={(e) => onChange("fatorLH", parseInt(e.target.value) || 0)}
                required
                placeholder="0"
                sx={inputSx}
              />
            </Grid>
          </Grid>

          {/* Linha: Controle solar, Bloqueio ruído, Arredondamento */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx}>Controle solar</InputLabel>
                <Select
                  value={vidro.controleSolar !== undefined ? (vidro.controleSolar ? "true" : "false") : ""}
                  label="Controle solar"
                  onChange={(e) => onChange("controleSolar", e.target.value === "true")}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {SIM_NAO_OPTIONS.map((opt) => (
                    <MenuItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx}>Bloqueio ruído</InputLabel>
                <Select
                  value={vidro.bloqueioRuido !== undefined ? (vidro.bloqueioRuido ? "true" : "false") : ""}
                  label="Bloqueio ruído"
                  onChange={(e) => onChange("bloqueioRuido", e.target.value === "true")}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {SIM_NAO_OPTIONS.map((opt) => (
                    <MenuItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Arredondamento</InputLabel>
                <Select
                  value={vidro.idArredondamento && arredondamentos.some(a => a.id === vidro.idArredondamento) ? vidro.idArredondamento : ""}
                  label="Arredondamento"
                  onChange={(e) => onChange("idArredondamento", e.target.value === "" ? undefined : Number(e.target.value))}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {arredondamentos.map((arr) => (
                    <MenuItem key={arr.id} value={arr.id}>
                      {arr.nome || arr.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
